import { Info, X } from 'lucide-react';
import { useState } from 'react';
import { TenantSelector } from '../../../components/tenant-selector/TenantSelector';
import type {
  TenantSelectionConflicts,
  TenantSelectorRowData,
} from '../../../components/tenant-selector/types';
import { DialogBackdrop } from '../../../components/ui/dialog-backdrop';
import { TenantSelectionHelpPanel } from '../edit-automation-template/tenant-selection-control/TenantSelectionHelpPanel';
import { TenantSelectionValidationStatus } from '../edit-automation-template/tenant-selection-control/TenantSelectionValidationStatus';
import styles from '../edit-automation-template/tenant-selection-control/TenantSelectionControl.module.css';
import type { TenantSelectionType } from '../automations-tab/templates.types';

const DIALOG_TITLE = 'Tenant Selection';
const DIALOG_SUBTITLE = 'Choose which tenants receive this segment when you save.';
const DRAFT_SELECTION_DESCRIPTION =
  'Not synced to anyone yet. Keep building — no tenant can target it until you sync it.';
const ALL_TENANTS_SELECTION_DESCRIPTION =
  "New tenants will automatically receive this segment when they're added.";
const SELECTED_TENANTS_SELECTION_DESCRIPTION =
  'Only the tenants you select below will receive this segment. New tenants will not receive it unless you add them or they are added to an automation which uses this segment.';

interface SegmentTenantSelectionDialogProps {
  onClose: () => void;
  onChange: (type: TenantSelectionType, ids: string[]) => void;
  tenantRowData: Record<string, TenantSelectorRowData>;
  conflicts: TenantSelectionConflicts | null;
  tenantSelectionType: TenantSelectionType;
  selectedTenantIds: string[];
  disabled?: boolean;
  isLoading?: boolean;
}

/**
 * The segment tenant-selection dialog. Mounted only while open by EditSegmentPage,
 * so its local editing copy starts fresh from the committed selection on each open —
 * no open prop and no state-snapshotting effect. Confirming commits via onChange; the
 * dialog closes itself imperatively through onClose.
 */
export function SegmentTenantSelectionDialog({
  onClose,
  onChange,
  tenantRowData,
  conflicts,
  tenantSelectionType,
  selectedTenantIds,
  disabled = false,
  isLoading,
}: SegmentTenantSelectionDialogProps) {
  const [localSelectionType, setLocalSelectionType] = useState<TenantSelectionType>(tenantSelectionType);
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedTenantIds);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleConfirm = () => {
    onChange(localSelectionType, localSelectedIds);
    onClose();
  };

  const handleLocalChange = (type: TenantSelectionType, ids: string[]) => {
    setLocalSelectionType(type);
    setLocalSelectedIds(ids);
  };

  const hasSelectedTenants = localSelectionType === 'selected' && localSelectedIds.length > 0;

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <hgroup>
            <h2>{DIALOG_TITLE}</h2>
            <p>{DIALOG_SUBTITLE}</p>
          </hgroup>
          <button
            type="button"
            onClick={() => setHelpOpen(o => !o)}
            className={styles.helpButton}
            aria-expanded={helpOpen}>
            <Info aria-hidden="true" />
            {helpOpen ? 'Hide explanation' : 'Learn more'}
          </button>
          <button type="button" onClick={onClose} className={styles.closeButton}>
            <X />
          </button>
        </header>

        <main>
          {helpOpen && <TenantSelectionHelpPanel context="segment" />}
          <TenantSelector
            tenantSelectionType={localSelectionType}
            selectedTenantIds={localSelectedIds}
            onChange={handleLocalChange}
            includeDraftOption
            draftSelectionDescription={DRAFT_SELECTION_DESCRIPTION}
            allTenantsSelectionDescription={ALL_TENANTS_SELECTION_DESCRIPTION}
            selectedTenantsSelectionDescription={SELECTED_TENANTS_SELECTION_DESCRIPTION}
            disabled={disabled}
            tenantRowData={tenantRowData}
            isLoading={isLoading}
          />

          {hasSelectedTenants && conflicts && (
            <TenantSelectionValidationStatus conflicts={conflicts} isValidating={false} />
          )}
        </main>

        <footer className={styles.dialogFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} className={styles.confirmButton}>
            OK
          </button>
        </footer>
      </dialog>
    </>
  );
}
